import React, { useState, useEffect, useRef } from "react";
import { Dropdown, DropdownToggle, DropdownMenu, Button, Input, Label, FormGroup } from "reactstrap";
import gql from "graphql-tag";
import { useQuery } from "@apollo/client";
import withApolloClient from "../../withApolloClient";

import ConfirmationPopup from "../../Material/ConfirmationPopup";
import { GroupsTypeAhead, RepsTypeAhead } from "./TypeAheadComponents";
import { BoardQuery } from "../Queries";
import "./BoardActionsMenu.css";
import { HasPermissionsQuery } from "../../Functions/querys";

const UpdateBoardMutation = gql`
  mutation UpdateBoardMutation($input: UpdateBoardMutationInput!) {
    updateBoard(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`;

const BoardActionsMenu = ({ boardId, apolloClient }) => {
    const [open, setOpen] = useState(false);
    const [activeSection, setActiveSection] = useState(null);

    const [newTitle, setNewTitle] = useState("");
    const [sharedWithUsers, setSharedWithUsers] = useState([]);
    const [sharedWithGroups, setSharedWithGroups] = useState([]);
    const [unsharedWithUsers, setUnsharedWithUsers] = useState([]);
    const [unsharedWithGroups, setUnsharedWithGroups] = useState([]);

    const prevUserIdsRef = useRef([]);
    const prevGroupIdsRef = useRef([]);

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState("");

    const { data } = useQuery(BoardQuery, {
        variables: { id: boardId },
        fetchPolicy: "network-only",
        skip: !boardId,
    });

    const { data: permData } = useQuery(HasPermissionsQuery, {
        variables: { userPermissions: ["change_board", "delete_board"] },
        fetchPolicy: "network-only",
    });

    const boarddata = data?.board;

    useEffect(() => {
        if (boarddata?.title) {
            setNewTitle(boarddata.title);
        }
        if (boarddata) {
            const groupIds =
                boarddata?.sharedWithGroups?.edges?.map((e) => e.node.id) || [];
            const userIds =
                boarddata?.sharedWithUsers?.edges?.map((e) => e.node.id) || [];

            setSharedWithGroups(groupIds);
            setSharedWithUsers(userIds);

            prevGroupIdsRef.current = groupIds;
            prevUserIdsRef.current = userIds;
        }
    }, [boarddata]);

    const userObjects =
        boarddata?.sharedWithUsers?.edges?.map((e) => e.node) || [];
    const selectedUserObjects = userObjects.filter((u) =>
        sharedWithUsers.includes(u.id)
    );

    const updateBoard = async (values = {}) => {
        setLoading(true);
        setError("");
        try {
            const response = await apolloClient.mutate({
                mutation: UpdateBoardMutation,
                variables: { input: { boardId, ...values } },
                refetchQueries: ["Board", "AllBoards"],
            });
            if (!response?.data?.updateBoard?.ok) {
                setError(response?.data?.updateBoard?.errors?.[0]?.messages?.[0] || "Update failed");
            }
            return true;
        } catch (err) {
            setError(err.message || "Update failed");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const saveRename = async () => {
        if (!newTitle.trim()) return;

        const ok = await updateBoard({ title: newTitle.trim() });
        if (ok) {
            setActiveSection(null);
            setOpen(false);
        }
    };

    const saveUserPermissions = async () => {
        const ok = await updateBoard({
            sharedWithUserIds: sharedWithUsers,
            unshareWithUserIds: unsharedWithUsers
        });
        if (ok) { setActiveSection(null); setOpen(false); }
    };

    const saveGroupPermissions = async () => {
        const ok = await updateBoard({
            sharedWithGroupIds: sharedWithGroups,
            unshareWithGroupIds: unsharedWithGroups
        });
        if (ok) { setActiveSection(null); setOpen(false); }
    };


    const handleUsersChange = (updatedIds) => {
        setSharedWithUsers(updatedIds);

        const removed = prevUserIdsRef.current.filter(
            (id) => !updatedIds.includes(id)
        );
        if (removed.length) {
            setUnsharedWithUsers((prev) => [...prev, ...removed]);
        }
        prevUserIdsRef.current = updatedIds;
    };

    const handleGroupsChange = (updatedIds) => {
        setSharedWithGroups(updatedIds);

        const removed = prevGroupIdsRef.current.filter(
            (id) => !updatedIds.includes(id)
        );
        if (removed.length) {
            setUnsharedWithGroups((prev) => [...prev, ...removed]);
        }
        prevGroupIdsRef.current = updatedIds;
    };

    const handleArchive = async () => {
        const ok = await updateBoard({ isArchived: true });
        if (ok) {
            setOpenModal("");
            setOpen(false);
        }
    };

    const toggleMenu = () => {
        if (open) {
            setActiveSection(null);
        }
        setOpen(!open);
    };

    if (!boardId || !permData?.hasPermissions.includes("change_board")) return null;

    return (
        <>
            <Dropdown isOpen={open} toggle={toggleMenu} direction="down">
                <DropdownToggle
                    tag="span"
                    style={{ cursor: "pointer" }}
                    className="menu-trigger"
                >
                    <i className="fa fa-ellipsis-h fa-2x" />
                </DropdownToggle>
                <DropdownMenu className="board-action-main-menu">
                    {!activeSection && (
                        <>
                            <div className="menu-item" onClick={() => setActiveSection("rename")}>
                                <i className="fa fa-pencil" /> Rename
                            </div>
                            <div className="menu-item" onClick={() => setActiveSection("group")}>
                                <i className="fa fa-users" /> Update Group Permission
                            </div>
                            <div className="menu-item" onClick={() => setActiveSection("user")}>
                                <i className="fa fa-user" /> Update User Permission
                            </div>
                            {permData?.hasPermissions.includes("delete_board") &&
                                <>
                                    <hr className="m-0" />
                                    <div className="menu-item text-danger" onClick={() => setOpenModal("archiveBoard")}>
                                        <i className="fa fa-archive text-danger" /> Archive
                                    </div>
                                </>
                            }
                        </>
                    )}

                    {/* RENAME SUBMENU */}
                    {activeSection === "rename" && (
                        <div className="submenu">
                            <FormGroup>
                                <Label>Board Name</Label>
                                <Input
                                    value={newTitle}
                                    onChange={(e) => setNewTitle(e.target.value)}
                                    placeholder="Enter new board name"
                                    autoFocus
                                />
                                {error && <div className="text-danger mt-1">{error}</div>}
                            </FormGroup>
                            <div className="submenu-actions">
                                <Button size="sm" type="button" onClick={() => setActiveSection(null)} disabled={loading}>
                                    Cancel
                                </Button>
                                <Button size="sm" color="primary" type="button" onClick={saveRename} disabled={loading}>
                                    {loading ? "Saving..." : "Save"}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* GROUP PERMISSIONS SUBMENU */}
                    {activeSection === "group" && (
                        <div className="submenu">
                            <Label>Share with Groups</Label>
                            <GroupsTypeAhead defaultValue={sharedWithGroups} onChange={handleGroupsChange} />
                            {error && <div className="text-danger mt-1">{error}</div>}
                            <div className="submenu-actions">
                                <Button size="sm" type="button" onClick={() => setActiveSection(null)} disabled={loading}>
                                    Cancel
                                </Button>
                                <Button size="sm" color="primary" type="button" onClick={saveGroupPermissions} disabled={loading}>
                                    {loading ? "Saving..." : "Save"}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* USER PERMISSIONS SUBMENU */}
                    {activeSection === "user" && (
                        <div className="submenu">
                            <Label>Share with Users</Label>
                            <RepsTypeAhead defaultValue={selectedUserObjects} selected={selectedUserObjects} onChange={handleUsersChange} />
                            {error && <div className="text-danger mt-1">{error}</div>}
                            <div className="submenu-actions">
                                <Button size="sm" type="button" onClick={() => setActiveSection(null)} disabled={loading}>
                                    Cancel
                                </Button>
                                <Button size="sm" color="primary" type="button" onClick={saveUserPermissions} disabled={loading}>
                                    {loading ? "Saving..." : "Save"}
                                </Button>
                            </div>
                        </div>
                    )}
                </DropdownMenu>
            </Dropdown>

            {/* ARCHIVE CONFIRMATION MODAL */}
            <ConfirmationPopup
                open={openModal === "archiveBoard"}
                message={"Are you sure you want to archive this board?"}
                loading={loading}
                handleClose={() => setOpenModal("")}
                action={"Yes"}
                confirmAction={handleArchive}
                color="danger"
                error={error}
            />
        </>
    );
};

export default withApolloClient(BoardActionsMenu);
